/**
 * Cliente da API (fetch nativo): base por VITE_API_URL (dev: http://127.0.0.1:3000)
 * ou mesma origem (produção — Fastify serve o build).
 *
 * Erros da API (409/422/404) viram `ErroApi` com o corpo parseado — o gate e a
 * revalidação devolvem listas de problemas (problemas/diagnosticos/bloqueantes)
 * que a UI renderiza completas.
 */

export const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly corpo: unknown,
  ) {
    super(`Erro ${status} da API`);
  }
}

// ---------------------------------------------------------------------------
// Autenticação (tela de login — senha fixa de acesso): token em localStorage,
// enviado em todo request; 401 → a App mostra a tela de login (callback).
// ---------------------------------------------------------------------------

const CHAVE_TOKEN = "token_acesso";

export function tokenAtual(): string | null {
  return localStorage.getItem(CHAVE_TOKEN);
}

export function gravarToken(token: string): void {
  localStorage.setItem(CHAVE_TOKEN, token);
}

export function limparToken(): void {
  localStorage.removeItem(CHAVE_TOKEN);
}

/** URL com o token na query — para os poucos pontos que carregam binário por
 *  URL direta (<img>, <iframe>, âncora de download), que não enviam header. */
export function urlComToken(caminho: string): string {
  const base = `${API_BASE}${caminho}`;
  const token = tokenAtual();
  if (!token) return base;
  return `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

/** Callback registrado pela App: qualquer 401 da API derruba para o login. */
let ao401: (() => void) | null = null;
export function aoReceber401(cb: (() => void) | null): void {
  ao401 = cb;
}

async function chamar<T>(metodo: string, caminho: string, corpo?: unknown): Promise<T> {
  const token = tokenAtual();
  const resp = await fetch(`${API_BASE}${caminho}`, {
    method: metodo,
    headers: {
      ...(corpo !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: corpo !== undefined ? JSON.stringify(corpo) : undefined,
  });
  const texto = await resp.text();
  let json: unknown = null;
  try {
    json = texto ? JSON.parse(texto) : null;
  } catch {
    json = texto;
  }
  if (resp.status === 401 && !caminho.startsWith("/login")) ao401?.();
  if (!resp.ok) throw new ErroApi(resp.status, json);
  return json as T;
}

export const api = {
  get: <T>(caminho: string) => chamar<T>("GET", caminho),
  post: <T>(caminho: string, corpo?: unknown) => chamar<T>("POST", caminho, corpo),
  put: <T>(caminho: string, corpo?: unknown) => chamar<T>("PUT", caminho, corpo),
  patch: <T>(caminho: string, corpo?: unknown) => chamar<T>("PATCH", caminho, corpo),
  del: <T>(caminho: string) => chamar<T>("DELETE", caminho),
};

/** Detalhes de erro que a API pode devolver em listas — normalizado para a UI. */
export function listasDoErro(corpo: unknown): { titulo: string; itens: string[] }[] {
  if (!corpo || typeof corpo !== "object") return [];
  const c = corpo as Record<string, unknown>;
  const listas: { titulo: string; itens: string[] }[] = [];
  if (typeof c.erro === "string") listas.push({ titulo: "Erro", itens: [c.erro] });
  for (const [chave, titulo] of [
    ["problemas", "Problemas"],
    ["bloqueantes", "Divergências bloqueantes"],
    ["problemasGerais", "Problemas gerais"],
    ["elegiveis", "Motores elegíveis"],
  ] as const) {
    const v = c[chave];
    if (Array.isArray(v) && v.length > 0) listas.push({ titulo, itens: v.map(String) });
  }
  if (Array.isArray(c.diagnosticos)) {
    const itens = (c.diagnosticos as { motor: string; compativel: boolean; problemas: string[] }[]).flatMap(
      (d) => d.problemas.map((p) => `${d.motor}: ${p}`),
    );
    if (itens.length > 0) listas.push({ titulo: "Diagnóstico do gate", itens });
  }
  return listas;
}
